'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import { usePathname, useRouter } from 'next/navigation';
import { QRCodeSVG } from 'qrcode.react';
import { adminSecurityApi, getApiErrorCode, getApiErrorMessage, persistAuthToken, tryAdminTokenRefresh } from '@/lib/api';
import { resolveDashboardForUser } from '@/lib/role-dashboard';
import { safeReplace } from '@/lib/safe-redirect';
import {
  clearAdminSetupReason,
  isAdminTwoFactorSetupRequired,
  readAdminSetupReason,
  refreshAdminAuthState,
  useAuthStore,
} from '@/lib/auth-store';
import { Loader2, Shield, ShieldAlert, ShieldCheck } from 'lucide-react';

export type AdminSecurityView = 'two-factor' | 'sessions' | 'devices' | 'ip-restrictions' | 'security-events';

type Props = { view: AdminSecurityView };

const SEVERITY_STYLES: Record<string, string> = {
  LOW: 'bg-slate-100 text-slate-700',
  MEDIUM: 'bg-amber-100 text-amber-800',
  HIGH: 'bg-orange-100 text-orange-800',
  CRITICAL: 'bg-red-100 text-red-800',
};

function formatDt(iso?: string | null) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

async function syncAdminAuthAfterSecurityAction(
  router: ReturnType<typeof useRouter>,
  pathname: string | null
) {
  const user = await refreshAdminAuthState();
  if (user && !isAdminTwoFactorSetupRequired(user)) {
    safeReplace(router, pathname, resolveDashboardForUser(user));
    router.refresh();
  }
}

export function AdminSecurityPanel({ view }: Props) {
  const router = useRouter();
  const pathname = usePathname();
  const refreshAdminAuth = useAuthStore((state) => state.refreshAdminAuth);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  const [twoFactor, setTwoFactor] = useState<any>(null);
  const [setupQrUri, setSetupQrUri] = useState<string | null>(null);
  const [setupToken, setSetupToken] = useState<string | null>(null);
  const [recoveryCodes, setRecoveryCodes] = useState<string[]>([]);
  const [codeInput, setCodeInput] = useState('');
  const [setupSuccess, setSetupSuccess] = useState(false);
  const [codesSaved, setCodesSaved] = useState(false);
  const [twoFactorAction, setTwoFactorAction] = useState<'enable' | 'verify' | 'disable' | 'recovery' | null>(null);
  const twoFactorActionRef = useRef(false);
  const autoSetupStartedRef = useRef(false);

  const [sessions, setSessions] = useState<any[]>([]);
  const [devices, setDevices] = useState<any[]>([]);
  const [ipRows, setIpRows] = useState<any[]>([]);
  const [ipStatus, setIpStatus] = useState<any>(null);
  const [events, setEvents] = useState<any[]>([]);
  const [eventFilter, setEventFilter] = useState({ severity: '', event_type: '' });

  const [ipForm, setIpForm] = useState({ cidr: '', label: '' });

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      if (view === 'two-factor') {
        const res = await adminSecurityApi.twoFactorStatus();
        setTwoFactor(res.data?.data ?? null);
        return;
      }
      if (view === 'sessions') {
        const res = await adminSecurityApi.listSessions();
        setSessions(res.data?.data ?? []);
        return;
      }
      if (view === 'devices') {
        const res = await adminSecurityApi.listDevices();
        setDevices(res.data?.data ?? []);
        return;
      }
      if (view === 'ip-restrictions') {
        const [list, status] = await Promise.all([
          adminSecurityApi.listIpAllowlist(),
          adminSecurityApi.ipRestrictionStatus(),
        ]);
        setIpRows(list.data?.data ?? []);
        setIpStatus(status.data?.data ?? null);
        return;
      }
      if (view === 'security-events') {
        const res = await adminSecurityApi.listSecurityEvents({
          severity: eventFilter.severity || undefined,
          event_type: eventFilter.event_type || undefined,
        });
        const data = res.data?.data;
        setEvents(data?.data ?? data ?? []);
      }
    } catch (e) {
      setError(getApiErrorMessage(e));
    } finally {
      setLoading(false);
    }
  }, [view, eventFilter.severity, eventFilter.event_type]);

  useEffect(() => {
    load();
  }, [load]);

  const handleEnable2fa = async (forceRestart = false) => {
    if (twoFactorActionRef.current) return;
    twoFactorActionRef.current = true;
    setTwoFactorAction('enable');
    setError(null);
    setMessage(null);
    setRecoveryCodes([]);
    try {
      const res = await adminSecurityApi.twoFactorEnable(forceRestart);
      setSetupQrUri(res.data?.data?.provisioning_uri ?? null);
      setSetupToken(res.data?.data?.setup_token ?? null);
      setCodeInput('');
      setMessage('Scan the QR code with your authenticator app, then enter the 6-digit code below.');
    } catch (e) {
      setError(getApiErrorMessage(e));
    } finally {
      twoFactorActionRef.current = false;
      setTwoFactorAction(null);
    }
  };

  useEffect(() => {
    if (view !== 'two-factor') return;
    if (loading || twoFactorActionRef.current || autoSetupStartedRef.current) return;
    if (twoFactor?.enabled || !twoFactor?.setup_required || setupQrUri) return;

    autoSetupStartedRef.current = true;
    // Reuse any pending secret already cached for this user (forceRestart=false).
    // Only force a new secret when the previous one is known to be undecryptable.
    // Using force=true on every mount regenerates the secret after the user scanned
    // the QR and causes /admin/security/2fa/verify → 422 INVALID_CODE.
    const forceRestart = readAdminSetupReason() === 'invalid_2fa_secret';
    void handleEnable2fa(forceRestart);
  }, [loading, setupQrUri, twoFactor?.enabled, twoFactor?.setup_required, view]);

  const handleVerify2fa = async () => {
    if (twoFactorActionRef.current) return;
    if (!setupQrUri) {
      setError('Begin setup and scan the QR code before verifying.');
      return;
    }
    const code = codeInput.replace(/\s+/g, '').trim();
    if (!/^\d{6}$/.test(code)) {
      setError('Enter the current 6-digit code from your authenticator app.');
      return;
    }

    twoFactorActionRef.current = true;
    setTwoFactorAction('verify');
    setError(null);
    try {
      const res = await adminSecurityApi.twoFactorVerify(code, setupToken);
      if (typeof window !== 'undefined' && res.data?.data?.access_token) {
        persistAuthToken(res.data);
        clearAdminSetupReason();
      }
      setRecoveryCodes(res.data?.data?.recovery_codes ?? []);
      setSetupQrUri(null);
      setSetupToken(null);
      setCodeInput('');
      setSetupSuccess(true);
      setMessage('Two-factor authentication enabled. Please save your recovery codes securely.');
      await load();
      // We do NOT sync Admin Auth and redirect here. We wait for user to confirm saving codes.
    } catch (e) {
      if (getApiErrorCode(e) === 'TWO_FACTOR_SETUP_EXPIRED') {
        twoFactorActionRef.current = false;
        setTwoFactorAction(null);
        await handleEnable2fa(true);
        setError('Your QR code expired. A fresh setup QR code has been generated; scan it and enter the new code.');
      } else {
        setError(getApiErrorMessage(e));
      }
    } finally {
      twoFactorActionRef.current = false;
      setTwoFactorAction(null);
    }
  };

  const handleDisable2fa = async () => {
    if (!window.confirm('Disable two-factor authentication for this account?')) return;
    if (twoFactorActionRef.current) return;
    const code = codeInput.replace(/\s+/g, '').trim();
    if (!/^\d{6}$/.test(code)) {
      setError('Enter the current 6-digit authenticator code to disable two-factor authentication.');
      return;
    }

    twoFactorActionRef.current = true;
    setTwoFactorAction('disable');
    setError(null);
    try {
      await adminSecurityApi.twoFactorDisable(code);
      setCodeInput('');
      setMessage('Two-factor authentication disabled.');
      await load();
      await refreshAdminAuth();
      router.refresh();
    } catch (e) {
      setError(getApiErrorMessage(e));
    } finally {
      twoFactorActionRef.current = false;
      setTwoFactorAction(null);
    }
  };

  const handleRegenRecovery = async () => {
    const code = codeInput.replace(/\s+/g, '').trim();
    if (!/^\d{6}$/.test(code)) {
      setError('Enter the current 6-digit authenticator code to regenerate recovery codes.');
      return;
    }
    if (twoFactorActionRef.current) return;

    twoFactorActionRef.current = true;
    setTwoFactorAction('recovery');
    setError(null);
    setMessage(null);
    try {
      const res = await adminSecurityApi.twoFactorRecoveryCodes(code);
      setRecoveryCodes(res.data?.data?.recovery_codes ?? []);
      setCodeInput('');
      setMessage('New recovery codes generated. Store them securely — your old codes no longer work.');
      await refreshAdminAuth();
      router.refresh();
    } catch (e) {
      setError(getApiErrorMessage(e));
    } finally {
      twoFactorActionRef.current = false;
      setTwoFactorAction(null);
    }
  };

  const normalizedCode = codeInput.replace(/\s+/g, '').trim();
  const hasSixDigitCode = /^\d{6}$/.test(normalizedCode);
  const actionInFlight = twoFactorAction !== null;

  if (loading) {
    return (
      <div className="flex min-h-[240px] items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-moss" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {error && <p className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</p>}
      {message && <p className="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800">{message}</p>}

      {view === 'two-factor' && (
        <div className="rounded-2xl border border-nature-border bg-bg/80 p-6 shadow-sm">
          <div className="mb-4 flex items-center gap-2">
            <ShieldCheck className="h-5 w-5 text-moss" />
            <h3 className="text-lg font-semibold text-soot">Two-Factor Authentication</h3>
          </div>
          {setupSuccess ? (
            <div className="mt-4 rounded-xl border border-emerald-200 bg-emerald-50 p-6">
              <h4 className="mb-2 text-lg font-bold text-emerald-900">Setup Complete!</h4>
              <p className="mb-4 text-emerald-800">Your two-factor authentication is now enabled. Please save your recovery codes securely. You will need them if you lose access to your authenticator app.</p>
              
              <div className="mb-4 rounded-xl border border-emerald-300 bg-emerald-100 p-4 font-mono text-sm sm:columns-2">
                {recoveryCodes.map((c) => <div key={c} className="py-1 text-emerald-900">{c}</div>)}
              </div>

              <div className="mb-6 flex items-center gap-2">
                <input type="checkbox" id="codes-saved" checked={codesSaved} onChange={(e) => setCodesSaved(e.target.checked)} className="h-4 w-4 rounded border-emerald-400 text-emerald-600 focus:ring-emerald-500" />
                <label htmlFor="codes-saved" className="text-sm font-medium text-emerald-900">I have securely saved these recovery codes</label>
              </div>

              <button
                type="button"
                disabled={!codesSaved || actionInFlight}
                onClick={async () => {
                  if (twoFactorActionRef.current) return;
                  twoFactorActionRef.current = true;
                  setTwoFactorAction('verify');
                  await tryAdminTokenRefresh();
                  await syncAdminAuthAfterSecurityAction(router, pathname);
                  twoFactorActionRef.current = false;
                  setTwoFactorAction(null);
                }}
                className="rounded-lg bg-emerald-600 px-6 py-2.5 text-sm font-medium text-text-inverse disabled:cursor-not-allowed disabled:opacity-60"
              >
                Continue to dashboard
              </button>
            </div>
          ) : twoFactor?.setup_required ? (
            <>
              {setupQrUri && (
                <div className="mt-4 flex flex-col items-center rounded-xl bg-bg p-6">
                  <p className="mb-4 text-sm text-nature-muted">Scan with Google Authenticator, Authy, or similar</p>
                  <div className="rounded-lg bg-bg p-3 shadow-sm">
                    <QRCodeSVG value={setupQrUri} size={200} level="M" includeMargin />
                  </div>
                </div>
              )}
              
              <div className="mt-4 flex flex-wrap items-end gap-3">
                <input
                  type="text"
                  inputMode="numeric"
                  placeholder="6-digit code"
                  value={codeInput}
                  onChange={(e) => setCodeInput(e.target.value)}
                  className="rounded-lg border border-nature-border px-3 py-2 text-sm"
                />
                
                <button
                  type="button"
                  onClick={() => handleEnable2fa(Boolean(setupQrUri))}
                  disabled={actionInFlight}
                  className="rounded-lg border border-nature-border px-4 py-2 text-sm disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {twoFactorAction === 'enable' ? 'Starting...' : setupQrUri ? 'Regenerate QR' : 'Begin setup'}
                </button>
                {setupQrUri && (
                  <button
                    type="button"
                    onClick={handleVerify2fa}
                    disabled={actionInFlight || !hasSixDigitCode}
                    className="rounded-lg bg-emerald-600 px-4 py-2 text-sm text-text-inverse disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    {twoFactorAction === 'verify' ? 'Verifying...' : 'Verify & enable'}
                  </button>
                )}
              </div>
            </>
          ) : (
            <>
              <dl className="mt-4 grid gap-3 text-sm sm:grid-cols-2">
                <div><dt className="text-nature-muted">Enabled</dt><dd className="font-medium">{twoFactor?.enabled ? 'Yes' : 'No'}</dd></div>
                <div><dt className="text-nature-muted">Required</dt><dd className="font-medium">{twoFactor?.required ? 'Yes' : 'No'}</dd></div>
              </dl>
              
              {recoveryCodes.length > 0 && (
                <div className="mt-4 rounded-xl border border-amber-200 bg-amber-50 p-4">
                  <p className="mb-2 text-sm font-medium text-amber-900">Recovery codes (store securely)</p>
                  <ul className="grid gap-1 font-mono text-sm sm:grid-cols-2">
                    {recoveryCodes.map((c) => <li key={c}>{c}</li>)}
                  </ul>
                </div>
              )}

              <div className="mt-6">
                <h4 className="mb-2 text-sm font-medium text-soot">Regenerate recovery codes or disable</h4>
                <div className="flex flex-wrap items-end gap-3">
                  <input
                    type="text"
                    inputMode="numeric"
                    placeholder="6-digit code"
                    value={codeInput}
                    onChange={(e) => setCodeInput(e.target.value)}
                    className="rounded-lg border border-nature-border px-3 py-2 text-sm"
                  />
                  
                  {twoFactor?.can_disable && (
                    <button
                      type="button"
                      onClick={handleDisable2fa}
                      disabled={actionInFlight || !hasSixDigitCode}
                      className="rounded-lg border border-red-300 px-4 py-2 text-sm text-red-700 disabled:cursor-not-allowed disabled:opacity-60"
                    >
                      {twoFactorAction === 'disable' ? 'Disabling...' : 'Disable'}
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={handleRegenRecovery}
                    disabled={actionInFlight || !hasSixDigitCode}
                    className="rounded-lg border border-nature-border px-4 py-2 text-sm disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    {twoFactorAction === 'recovery' ? 'Regenerating...' : 'Regenerate recovery codes'}
                  </button>
                </div>
              </div>

              <div className="mt-6 pt-6 border-t border-nature-border">
                <button
                  type="button"
                  onClick={() => router.replace(resolveDashboardForUser(useAuthStore.getState().user))}
                  className="text-sm font-medium text-moss hover:underline"
                >
                  &larr; Back to dashboard
                </button>
              </div>
            </>
          )}
        </div>
      )}

      {view === 'sessions' && (
        <div className="rounded-2xl border border-nature-border bg-bg/80 p-6">
          <div className="mb-4 flex items-center justify-between">
            <h3 className="text-lg font-semibold text-soot">Active sessions</h3>
            <button
              type="button"
              className="rounded-lg border border-red-200 px-3 py-1.5 text-sm text-red-700"
              onClick={async () => {
                if (!window.confirm('Revoke all sessions? You will need to sign in again.')) return;
                await adminSecurityApi.revokeAllSessions();
                await load();
              }}
            >
              Revoke all
            </button>
          </div>
          <ul className="divide-y divide-nature-border">
            {sessions.map((s) => (
              <li key={s.id} className="flex flex-wrap items-center justify-between gap-2 py-3 text-sm">
                <div>
                  <p className="font-medium">{s.device_name ?? 'Unknown device'}</p>
                  <p className="text-nature-muted">{s.browser} · {s.ip_address} · Last active {formatDt(s.last_activity_at)}</p>
                </div>
                <button
                  type="button"
                  className="text-red-600 hover:underline"
                  onClick={async () => {
                    await adminSecurityApi.revokeSession(s.id);
                    await load();
                  }}
                >
                  Revoke
                </button>
              </li>
            ))}
            {sessions.length === 0 && <li className="py-6 text-center text-nature-muted">No active sessions.</li>}
          </ul>
        </div>
      )}

      {view === 'devices' && (
        <div className="rounded-2xl border border-nature-border bg-bg/80 p-6">
          <h3 className="mb-4 text-lg font-semibold text-soot">Trusted devices</h3>
          <ul className="divide-y divide-nature-border">
            {devices.map((d) => (
              <li key={d.id} className="flex flex-wrap items-center justify-between gap-2 py-3 text-sm">
                <div>
                  <p className="font-medium">{d.device_name}</p>
                  <p className="text-nature-muted">
                    {d.trusted ? 'Trusted' : 'New / untrusted'} · Last seen {formatDt(d.last_seen_at)}
                  </p>
                </div>
                <button
                  type="button"
                  className="text-red-600 hover:underline"
                  onClick={async () => {
                    await adminSecurityApi.revokeDevice(d.id);
                    await load();
                  }}
                >
                  Revoke trust
                </button>
              </li>
            ))}
            {devices.length === 0 && <li className="py-6 text-center text-nature-muted">No devices recorded.</li>}
          </ul>
        </div>
      )}

      {view === 'ip-restrictions' && (
        <div className="space-y-4">
          <div className="rounded-2xl border border-nature-border bg-bg/80 p-6">
            <div className="mb-2 flex items-center gap-2">
              <Shield className="h-5 w-5 text-moss" />
              <h3 className="text-lg font-semibold text-soot">IP restrictions</h3>
            </div>
            <p className="text-sm text-nature-muted">
              Master whitelist: {ipStatus?.whitelist_enabled ? 'Enabled' : 'Disabled'} · Active entries: {ipStatus?.entry_count ?? 0}
            </p>
          </div>
          <div className="rounded-2xl border border-nature-border bg-bg/80 p-6">
            <form
              className="mb-4 flex flex-wrap gap-2"
              onSubmit={async (e) => {
                e.preventDefault();
                await adminSecurityApi.storeIpAllowlist(ipForm);
                setIpForm({ cidr: '', label: '' });
                await load();
              }}
            >
              <input required placeholder="CIDR e.g. 10.0.0.0/8" value={ipForm.cidr} onChange={(e) => setIpForm({ ...ipForm, cidr: e.target.value })} className="rounded-lg border px-3 py-2 text-sm" />
              <input placeholder="Label" value={ipForm.label} onChange={(e) => setIpForm({ ...ipForm, label: e.target.value })} className="rounded-lg border px-3 py-2 text-sm" />
              <button type="submit" className="rounded-lg bg-moss px-4 py-2 text-sm text-text-inverse">Add</button>
            </form>
            <ul className="divide-y divide-nature-border">
              {ipRows.map((row) => (
                <li key={row.id} className="flex items-center justify-between py-2 text-sm">
                  <span><strong>{row.cidr}</strong>{row.label ? ` — ${row.label}` : ''}</span>
                  <button type="button" className="text-red-600" onClick={async () => { await adminSecurityApi.destroyIpAllowlist(row.id); await load(); }}>Remove</button>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}

      {view === 'security-events' && (
        <div className="rounded-2xl border border-nature-border bg-bg/80 p-6">
          <div className="mb-4 flex flex-wrap items-center gap-2">
            <ShieldAlert className="h-5 w-5 text-orange-600" />
            <h3 className="text-lg font-semibold text-soot">Security events</h3>
            <select value={eventFilter.severity} onChange={(e) => setEventFilter({ ...eventFilter, severity: e.target.value })} className="ml-auto rounded-lg border px-2 py-1 text-sm">
              <option value="">All severities</option>
              {['LOW', 'MEDIUM', 'HIGH', 'CRITICAL'].map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
            <input placeholder="Filter type" value={eventFilter.event_type} onChange={(e) => setEventFilter({ ...eventFilter, event_type: e.target.value })} className="rounded-lg border px-2 py-1 text-sm" />
            <button type="button" onClick={load} className="rounded-lg border px-3 py-1 text-sm">Apply</button>
          </div>
          <ul className="divide-y divide-nature-border">
            {events.map((ev) => (
              <li key={ev.id} className="py-3 text-sm">
                <div className="flex flex-wrap items-center gap-2">
                  <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${SEVERITY_STYLES[ev.severity] ?? 'bg-slate-100'}`}>{ev.severity}</span>
                  <span className="font-medium">{ev.event_type}</span>
                  <span className="text-nature-muted">{formatDt(ev.created_at)}</span>
                </div>
                {ev.ip_address && <p className="mt-1 text-nature-muted">IP: {ev.ip_address}</p>}
              </li>
            ))}
            {events.length === 0 && <li className="py-6 text-center text-nature-muted">No security events.</li>}
          </ul>
        </div>
      )}
    </div>
  );
}
